import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, BalanceCard, ExpenseItem, SummaryCard } from '../components';
import './GroupDashboard.css';

interface GroupMember {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  balance: number;
}

interface Expense {
  id: string;
  description: string;
  amount: number;
  paidBy: string;
  paidByName: string;
  date: string;
  splitType: 'equal' | 'percentage' | 'custom';
  participants: string[];
  category?: string;
}

interface Group {
  id: string;
  name: string;
  description: string;
  currency: string;
  members: GroupMember[];
  expenses: Expense[];
  totalExpenses: number;
  createdAt: string;
}

const GroupDashboard: React.FC = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const [group, setGroup] = useState<Group | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'expenses' | 'settlements'>('overview');

  // Mock data for demonstration
  const mockGroup: Group = {
    id: 'group_1',
    name: 'Weekend Trip',
    description: 'Group for tracking weekend getaway expenses',
    currency: 'USD',
    totalExpenses: 1247.50,
    createdAt: '2024-01-15T10:00:00Z',
    members: [
      { id: 'user_1', name: 'Sarah Johnson', email: 'sarah@email.com', balance: 45.50 },
      { id: 'user_2', name: 'Alex Chen', email: 'alex@email.com', balance: -32.25 },
      { id: 'user_3', name: 'Jordan Smith', email: 'jordan@email.com', balance: -13.25 },
      { id: 'user_4', name: 'Sam Wilson', email: 'sam@email.com', balance: 0 }
    ],
    expenses: [
      {
        id: 'exp_1',
        description: 'Hotel Booking',
        amount: 480.00,
        paidBy: 'user_1',
        paidByName: 'Sarah Johnson',
        date: '2024-01-20T14:30:00Z',
        splitType: 'equal',
        participants: ['user_1', 'user_2', 'user_3', 'user_4'],
        category: 'accommodation'
      },
      {
        id: 'exp_2',
        description: 'Dinner at Restaurant',
        amount: 128.00,
        paidBy: 'user_2',
        paidByName: 'Alex Chen',
        date: '2024-01-20T19:00:00Z',
        splitType: 'equal',
        participants: ['user_1', 'user_2', 'user_3', 'user_4'],
        category: 'food'
      },
      {
        id: 'exp_3',
        description: 'Gas for Road Trip',
        amount: 85.50,
        paidBy: 'user_1',
        paidByName: 'Sarah Johnson',
        date: '2024-01-19T08:00:00Z',
        splitType: 'equal',
        participants: ['user_1', 'user_2', 'user_3', 'user_4'],
        category: 'transportation'
      },
      {
        id: 'exp_4',
        description: 'Activity Tickets',
        amount: 240.00,
        paidBy: 'user_3',
        paidByName: 'Jordan Smith',
        date: '2024-01-20T10:00:00Z',
        splitType: 'equal',
        participants: ['user_1', 'user_2', 'user_3', 'user_4'],
        category: 'entertainment'
      },
      {
        id: 'exp_5',
        description: 'Groceries',
        amount: 74.00,
        paidBy: 'user_4',
        paidByName: 'Sam Wilson',
        date: '2024-01-19T16:00:00Z',
        splitType: 'equal',
        participants: ['user_1', 'user_2', 'user_3', 'user_4'],
        category: 'food'
      }
    ]
  };

  useEffect(() => {
    // Simulate loading group data
    const loadGroupData = async () => {
      setIsLoading(true);
      try {
        // In a real app, this would fetch from localStorage or API
        await new Promise(resolve => setTimeout(resolve, 1000));
        setGroup(mockGroup);
      } catch (error) {
        console.error('Error loading group:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadGroupData();
  }, [groupId]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: group?.currency || 'USD'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getBalanceColor = (balance: number) => {
    if (balance > 0) return 'positive';
    if (balance < 0) return 'negative';
    return 'neutral';
  };

  const getCategoryIcon = (category?: string) => {
    switch (category) {
      case 'accommodation': return '🏨';
      case 'food': return '🍽️';
      case 'transportation': return '🚗';
      case 'entertainment': return '🎉';
      case 'utilities': return '💡';
      default: return '💰';
    }
  };

  if (isLoading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading group dashboard...</p>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="dashboard-error">
        <h2>Group Not Found</h2>
        <p>The group you're looking for doesn't exist or you don't have access to it.</p>
        <Button onClick={() => navigate('/')} variant="primary">
          Go Home
        </Button>
      </div>
    );
  }

  return (
    <div className="dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="group-info">
            <h1 className="group-name">{group.name}</h1>
            <p className="group-description">{group.description}</p>
          </div>
          <div className="header-actions">
            <Button 
              onClick={() => navigate(`/expense/add?groupId=${group.id}`)}
              variant="primary"
              size="medium"
            >
              Add Expense
            </Button>
            <Button 
              onClick={() => navigate(`/group/${group.id}/invite`)}
              variant="secondary"
              size="medium"
            >
              Invite Members
            </Button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="dashboard-tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab ${activeTab === 'expenses' ? 'active' : ''}`}
          onClick={() => setActiveTab('expenses')}
        >
          Expenses
        </button>
        <button 
          className={`tab ${activeTab === 'settlements' ? 'active' : ''}`}
          onClick={() => setActiveTab('settlements')}
        >
          Settlements
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            {/* Summary Cards */}
            <div className="summary-cards">
              <SummaryCard
                title="Total Expenses"
                value={formatCurrency(group.totalExpenses)}
                subtitle={`Across ${group.expenses.length} expenses`}
                icon="💰"
              />

              <SummaryCard
                title="Group Members"
                value={group.members.length}
                subtitle="Active participants"
                icon="👥"
              />

              <SummaryCard
                title="Outstanding"
                value={formatCurrency(group.members.reduce((sum, member) => sum + Math.abs(member.balance), 0) / 2)}
                subtitle="Total to settle"
                icon="⚖️"
              />
            </div>

            {/* Balance Overview */}
            <div className="balance-section">
              <h3>Member Balances</h3>
              <div className="balance-grid">
                {group.members.map((member) => (
                  <BalanceCard
                    key={member.id}
                    member={member}
                    showActions={false}
                  />
                ))}
              </div>
            </div>

            {/* Recent Activity */}
            <div className="recent-activity">
              <h3>Recent Activity</h3>
              <div className="activity-list">
                {group.expenses.slice(0, 5).map((expense) => (
                  <ExpenseItem
                    key={expense.id}
                    expense={expense}
                    currency={group.currency}
                    compact={true}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'expenses' && (
          <div className="expenses-tab">
            <div className="expenses-header">
              <h3>All Expenses</h3>
              <Button 
                onClick={() => navigate(`/expense/add?groupId=${group.id}`)}
                variant="primary"
                size="small"
              >
                Add New
              </Button>
            </div>
            
            <div className="expenses-list">
              {group.expenses.map((expense) => (
                <ExpenseItem
                  key={expense.id}
                  expense={expense}
                  currency={group.currency}
                  onView={(expenseId) => navigate(`/expense/${expenseId}`)}
                  showActions={true}
                />
              ))}
            </div>
          </div>
        )}

        {activeTab === 'settlements' && (
          <div className="settlements-tab">
            <div className="settlements-header">
              <h3>Debt Settlements</h3>
              <p>Track who owes what and mark payments as settled</p>
            </div>
            
            <div className="settlements-list">
              {group.members
                .filter(member => member.balance !== 0)
                .map((member) => (
                  <BalanceCard
                    key={member.id}
                    member={member}
                    onSettleUp={(memberId) => navigate(`/settlement?groupId=${group.id}&memberId=${memberId}`)}
                    onMarkPaid={(memberId) => navigate(`/settlement?groupId=${group.id}&memberId=${memberId}`)}
                    showActions={true}
                  />
                ))}
              
              {group.members.every(member => member.balance === 0) && (
                <div className="no-settlements">
                  <div className="no-settlements-icon">✅</div>
                  <h4>All Settled Up!</h4>
                  <p>Everyone's balances are current. No outstanding debts.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GroupDashboard; 